import React, { useState, useEffect, useRef } from "react";
import { Container, Col, Row, Card, Image } from "react-bootstrap";
import axios from "axios";
import { useHistory } from "react-router-dom";
import Marquee from "react-fast-marquee";
import web3 from "../Service/web3";
import "./counter.css";
import Fade from "react-reveal/Fade";
import Slide from "react-reveal/Slide";
import { colors } from "../../theme/colors";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CommonButton } from "./ui";
import { ArrowRight } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const Banner = ({ item, bookies, lotteries, buytotal, draws, wins, variant }) => {
  const [featuredNews, setFeaturedNews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [winnerNotifications, setWinnerNotifications] = useState([]);
  const autoBetRef = useRef(null);
  const heroBgRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_KEY;
  const history = useHistory();

  useEffect(() => {
    if (autoBetRef.current) {
      gsap.to(autoBetRef.current, {
        backgroundPosition: "200% center",
        duration: 3.5,
        ease: "none",
        repeat: -1,
        repeatDelay: 4,
      });
    }
  }, []);

  // ── Desktop-only parallax on the hero background layer ──────────
  useEffect(() => {
    // Skip on mobile/tablet — matches the ≤ 575px phone breakpoint
    const mm = window.matchMedia("(min-width: 576px)");
    if (!mm.matches || !heroBgRef.current) return;

    const st = gsap.to(heroBgRef.current, {
      // Move up at ~40% of the scroll speed → feels slower than page content
      yPercent: -15,
      // Subtle zoom-in as user scrolls down
      scale: 1.08,
      ease: "none",
      transformOrigin: "center center",
      scrollTrigger: {
        trigger: heroBgRef.current.closest(".banner-section"),
        start: "top top",
        end: "bottom top",
        scrub: 1.2,       // smooth lag behind scroll
        invalidateOnRefresh: true,
      },
    });

    return () => {
      // Clean up on unmount — prevents memory leaks / duplicate triggers
      st.scrollTrigger?.kill();
      st.kill();
    };
  }, []);
  // ────────────────────────────────────────────────────────────────

  useEffect(() => {
    axios.get(`${apiUrl}/news/getAllNews`).then((response) => {
      const newsData = response.data.result;
      const featuredNewsData = newsData.filter((news) => news.featured);
      setFeaturedNews(featuredNewsData);
    });

    const fetchData = async () => {
      try {
        let account = await web3.eth.getAccounts();
        let skip = 0;
        let allNotifications = [];
        let allWinnerNotifications = [];

        const fetchNotifications = async () => {
          const response = await fetch(
            `${apiUrl}/notifications?limit=10&skip=${skip}`,
            {
              method: "GET",
              headers: {
                Authorization: account[0],
              },
            }
          );
          if (response.ok) {
            const data = await response.json();
            const filteredNotifications = data.result.filter(
              (notification) =>
                notification.type === "LotteryCreatedAdmin" ||
                notification.type === "LotteryCreated"
            );
            if (filteredNotifications.length > 0) {
              allNotifications = [
                ...allNotifications,
                ...filteredNotifications,
              ];
              skip += 10;
              await fetchNotifications();
            } else {
              setNotifications(allNotifications);
            }

            const filteredWinnerNotifications = data.result.filter(
              (notification) =>
                notification.type === "LotteryResult" ||
                notification.type === "SpinLotteryResult" ||
                notification.type === "LotteryWinner"
            );
            if (filteredWinnerNotifications.length > 0) {
              allWinnerNotifications = [
                ...allWinnerNotifications,
                ...filteredWinnerNotifications,
              ];
              skip += 10;
              await fetchNotifications();
            } else {
              setWinnerNotifications(allWinnerNotifications);
            }
          } else {
            console.error("Error fetching notifications:", response.error);
          }
        };
        await fetchNotifications();
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    fetchData();
  }, []);

  function handleRedirect(notification) {
    switch (notification.type) {
      case "LotteryCreated":
        if (notification.lotteryName === "Missile") {
          history.push(`/missilelottery/${notification.genId}`);
        } else if (notification.lotteryName === "Mine") {
          history.push(`/minelottery/${notification.genId}`);
        } else if (notification.lotteryName === "MRL") {
          history.push(`/lottery/${notification.genId}`);
        } else {
          history.push("/spinnerlottery");
        }
        break;

      case "LotteryCreatedAdmin":
        if (notification.lotteryName === "Missile") {
          history.push(`/missilelottery/${notification.genId}`);
        } else if (notification.lotteryName === "Mine") {
          history.push(`/minelottery/${notification.genId}`);
        } else if (notification.lotteryName === "MRL") {
          history.push(`/lottery/${notification.genId}`);
        } else {
          history.push("/spinnerlottery");
        }
        break;

      default:
        break;
    }
  }

  return (
    <Container fluid className="p-0">
      {((featuredNews !== undefined && featuredNews.length > 0) ||
        (notifications !== undefined && notifications.length > 0)) && (
        <Row>
          <Col className="m-0 mt-3 p-0">
            <Marquee pauseOnHover={true} gradient={true} className="marq">
              {featuredNews.map((news) => (
                <span key={news.id} className="feature-msg mx-4">
                  <a href={`/news/${news.id}`}>{news.title}</a>
                </span>
              ))}
              {notifications.slice(0, 3).map((item) => (
                <span
                  key={item.id}
                  onClick={() => handleRedirect(item)}
                  className="feature-msg mx-4"
                >
                  ID-{item.genId}: "{item.title}"
                </span>
              ))}
              {winnerNotifications.slice(0, 3).map((item) => (
                <span
                  key={item.id}
                  onClick={() => history.push("/lottery-result")}
                  className="feature-msg mx-4"
                >
                  ID-{item.genId}: "{item.title}"
                </span>
              ))}
            </Marquee>
          </Col>
        </Row>
      )}
      <section className="banner-section">
        <div 
          ref={heroBgRef}
          className="hero-bg-layer"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            // backgroundImage: `url(${require("../../static/Images/heroImage.png")})`,
            backgroundSize: "cover",
            backgroundPosition: "right center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div className="banner-content-area" style={{ position: "relative", zIndex: 2 }}>
          <div className="container container-p0">
            <Fade left>
              <div className="row">
                <div className="col-md-6 col-lg-8">
                  <div className={`banner-content${variant === "home" ? " home-hero" : ""}`}>
                    {variant === "home" && (
                      <p className="home-kicker">Decentralized fundraising</p>
                    )}
                    <h1
                      className="title"
                      style={{ textAlign: "left", fontWeight: "600" }}
                    >
                      Join the <br />
                      <span 
                        ref={autoBetRef}
                        style={{ 
                          fontSize: "1.1em", 
                          fontWeight: "bold", 
                          textShadow: `0 0 10px ${colors.primary.main}4d`,
                          backgroundImage: `linear-gradient(120deg, ${colors.primary.main} 0%, ${colors.primary.main} 40%, ${colors.primary.lighter} 50%, ${colors.primary.main} 60%, ${colors.primary.main} 100%)`,
                          backgroundSize: "200% auto",
                          backgroundPosition: "-100% center",
                          color: "transparent",
                          WebkitBackgroundClip: "text",
                          backgroundClip: "text",
                          display: "inline-block",
                          willChange: "background-position",
                          transform: "translateZ(0)"
                        }}>AutoBET</span> Revolution <br />
                      in Decentralized Fundraising
                    </h1>
                    <p>
                      {variant === "home"
                        ? "Play on-chain lottery games, fund causes, and win with fully automated smart contracts on Polygon."
                        : "AutoBET is an innovative web3 fundraising platform with prewhite-labelled lottery products and Gift Card donation system. It is fully automated on smart-contracts that run on the Polygon Network. Currently, only POL is accepted as payment and plans are in place to integrate its native BET token as well as other major digital currencies."}
                    </p>
                    {variant === "home" && (
                      <div className="home-hero-actions" style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                        <CommonButton 
                          variant="contained" 
                          size="hero" 
                          endIcon={<ArrowRight size={18} color="white" />} 
                          onClick={() => window.location.href = "#game-arena"}
                        >
                          Play now
                        </CommonButton>
                        <CommonButton 
                          variant="outlined" 
                          size="hero" 
                          onClick={() => history.push("/all-lotteries")}
                          sx={{
                            color: "rgba(255, 255, 255, 0.85)",
                            borderColor: "rgba(255, 255, 255, 0.3)",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderColor: "rgba(255, 255, 255, 0.6)"
                            }
                          }}
                        >
                          Browse all games
                        </CommonButton>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Fade>
            <div className={`counter-wrapper${variant === "home" ? " home-hero-stats" : ""}`}>
              <div className="row ">
                <div className="col-lg-2 col-4 ">
                  <Fade bottom delay={100}>
                    <div className="single-counter counter-color-5 d-flex align-items-center justify-content-center">
                      <div className="counter-items text-center">
                        <span className="count">{bookies}</span>
                        <p className="text">Creators</p>
                      </div>
                    </div>
                  </Fade>
                </div>
                <div className="col-lg-2 col-4">
                  <Fade bottom delay={200}>
                    <div className="single-counter counter-color-4 d-flex align-items-center justify-content-center">
                      <div className="counter-items text-center">
                        <span className="count">{lotteries}</span>
                        <p className="text">Lotteries</p>
                      </div>
                    </div>
                  </Fade>
                </div>
                <div className="col-lg-2 col-4">
                  <Fade bottom delay={300}>
                    <div className="single-counter counter-color-3 d-flex align-items-center justify-content-center">
                      <div className="counter-items text-center">
                        <span className="count">{buytotal}</span>
                        <p className="text">Tickets sold</p>
                      </div>
                    </div>
                  </Fade>
                </div>
                <div className="col-lg-2 col-4">
                  <Fade bottom delay={400}>
                    <div className="single-counter counter-color-2 d-flex align-items-center justify-content-center">
                      <div className="counter-items text-center">
                        <span className="count">{draws}</span>
                        <p className="text">Draws</p>
                      </div>
                    </div>
                  </Fade>
                </div>
                <div className="col-lg-2 col-4">
                  <Fade bottom delay={500}>
                    <div className="single-counter counter-color-1 d-flex align-items-center justify-content-center">
                      <div className="counter-items text-center">
                        <span className="count">{wins}</span>
                        <p className="text">Wins</p>
                      </div>
                    </div>
                  </Fade>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Container>
  );
};
export default Banner;
